import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Building2, CalendarPlus, ChevronDown, Eye, HardHat, Loader2, MapPin, Pencil, Send, User } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '../../services/tokenService';
import { VisionneuseProtegee } from '../documents/VisionneuseProtegee';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fr = (d) => (d ? d.split('-').reverse().join('/') : '—');
const COULEURS = { 'En cours': 'bg-emerald-100 text-emerald-800', 'À venir': 'bg-sky-100 text-sky-800', Terminé: 'bg-slate-100 text-slate-600' };

const auth = () => ({ Authorization: `Bearer ${getToken()}` });
const frDT = (iso) => new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

const EnvoiAttestation = ({ c, emailMo, onEnvoye }) => {
  const [busy, setBusy] = useState(false);
  const envoyer = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/maitres-ouvrage/carnet/${c.souscription_id}/envoyer-attestation`, {}, { headers: auth() });
      toast.success(`Attestation envoyée à ${r.data.envoye_a} (copie à vous)`);
      onEnvoye();
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col items-end gap-0.5">
      <button type="button" onClick={envoyer} disabled={busy || !emailMo} title={emailMo ? `Envoyer à ${emailMo}` : "Ajoutez d'abord l'e-mail du maître d'ouvrage"}
        className="inline-flex items-center gap-1 rounded-full bg-[#0a3d62] px-3 py-1 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40" data-testid={`carnet-envoyer-${c.souscription_id}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Envoyer au maître d'ouvrage
      </button>
      {c.dernier_envoi_mo && <span className="text-[10px] text-slate-400" data-testid={`carnet-dernier-envoi-${c.souscription_id}`}>Envoyée le {frDT(c.dernier_envoi_mo)}</span>}
    </div>
  );
};

const Coordonnees = ({ mo, onMaj }) => {
  const [edit, setEdit] = useState(false);
  const [tel, setTel] = useState(mo.telephone || '');
  const [mail, setMail] = useState(mo.email || '');
  const [busy, setBusy] = useState(false);
  const enregistrer = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/maitres-ouvrage/carnet`, { cle: mo.cle, telephone: tel, email: mail }, { headers: auth() });
      toast.success(`Coordonnées mises à jour sur ${r.data.chantiers_mis_a_jour} chantier(s)`);
      setEdit(false);
      onMaj();
    } catch (e) { toast.error(e.response?.data?.detail || 'Mise à jour impossible'); } finally { setBusy(false); }
  };
  if (!edit) {
    return (
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 py-2 text-xs text-slate-600">
        <span>Tél. : <b>{mo.telephone || '—'}</b></span><span>E-mail : <b>{mo.email || '—'}</b></span>
        <button type="button" onClick={() => setEdit(true)} className="inline-flex items-center gap-1 text-[#0a3d62] hover:underline" data-testid="carnet-mo-modifier">
          <Pencil className="h-3 w-3" /> Corriger
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-end gap-2 border-b border-slate-100 py-2" data-testid="carnet-mo-form">
      <input value={tel} onChange={(e) => setTel(e.target.value)} placeholder="Téléphone" className="h-8 rounded-md border border-slate-200 px-2 text-xs" data-testid="carnet-mo-tel" />
      <input type="email" value={mail} onChange={(e) => setMail(e.target.value)} placeholder="E-mail" className="h-8 w-56 rounded-md border border-slate-200 px-2 text-xs" data-testid="carnet-mo-email" />
      <button type="button" onClick={enregistrer} disabled={busy} className="h-8 rounded-full bg-[#0a3d62] px-3 text-xs font-semibold text-white disabled:opacity-50" data-testid="carnet-mo-enregistrer">
        {busy ? '…' : 'Enregistrer'}
      </button>
      <button type="button" onClick={() => setEdit(false)} className="h-8 px-2 text-xs text-slate-500" data-testid="carnet-mo-annuler">Annuler</button>
      <p className="w-full text-[10px] text-slate-400">Appliqué à tous les chantiers de ce maître d'ouvrage ; les attestations déjà émises ne changent pas.</p>
    </div>
  );
};

const Chantier = ({ c, onVoir, emailMo, onMaj }) => (
  <li className="flex flex-wrap items-start gap-3 border-t border-slate-100 py-3 first:border-t-0" data-testid={`carnet-chantier-${c.souscription_id}`}>
    <HardHat className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-slate-800">{c.description || c.operation || 'Chantier'}</p>
      {c.adresse && <p className="flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3 w-3" />{c.adresse}</p>}
      <p className="text-xs text-slate-500">Du {fr(c.date_debut)} au {fr(c.date_fin)} · {c.numero_police}</p>
    </div>
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${COULEURS[c.statut] || ''}`} data-testid={`carnet-statut-${c.souscription_id}`}>{c.statut}</span>
    <button type="button" onClick={() => onVoir(c)} className="inline-flex items-center gap-1 rounded-full border border-[#0a3d62] px-3 py-1 text-xs font-semibold text-[#0a3d62] transition-colors hover:bg-[#0a3d62] hover:text-white" data-testid={`carnet-attestation-${c.souscription_id}`}>
      <Eye className="h-3.5 w-3.5" /> Attestation
    </button>
    {c.statut === 'En cours' && (
      <a href={`/calculateur-batico?prolonger=${c.souscription_id}`} className="inline-flex items-center gap-1 rounded-full bg-[#DEA900] px-3 py-1 text-xs font-semibold text-white transition-opacity hover:opacity-90" data-testid={`carnet-prolonger-${c.souscription_id}`}>
        <CalendarPlus className="h-3.5 w-3.5" /> Prolonger
      </a>
    )}
    <EnvoiAttestation c={c} emailMo={emailMo} onEnvoye={onMaj} />
  </li>
);

const MaitreOuvrage = ({ mo, onVoir, onMaj }) => {
  const [ouvert, setOuvert] = useState(false);
  const nom = mo.raison_sociale || `${mo.prenom} ${mo.nom}`.trim();
  const Icon = mo.raison_sociale ? Building2 : User;
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="carnet-mo">
      <button type="button" onClick={() => setOuvert(!ouvert)} className="flex w-full items-center gap-3 px-4 py-3 text-left" data-testid="carnet-mo-toggle">
        <Icon className="h-5 w-5 text-[#0a3d62]" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-[#0a3d62]">{nom}</p>
          <p className="truncate text-xs text-slate-500">{[mo.ville, mo.telephone, mo.email].filter(Boolean).join(' · ')}</p>
        </div>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">{mo.chantiers.length} chantier{mo.chantiers.length > 1 ? 's' : ''}</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${ouvert ? 'rotate-180' : ''}`} />
      </button>
      {ouvert && (
        <div className="px-4 pb-2">
          <Coordonnees mo={mo} onMaj={onMaj} />
          <ul>{mo.chantiers.map((c) => <Chantier key={c.souscription_id} c={c} onVoir={onVoir} emailMo={mo.email} onMaj={onMaj} />)}</ul>
        </div>
      )}
    </div>
  );
};

/** Espace membre : carnet des maîtres d'ouvrage et de leurs chantiers assurés. */
export default function MesMaitresOuvrage({ getAuthHeader }) {
  const [liste, setListe] = useState(null);
  const [doc, setDoc] = useState(null);
  const charger = () => axios.get(`${API}/maitres-ouvrage/carnet`, { headers: getAuthHeader() })
    .then((r) => setListe(r.data.maitres_ouvrage || [])).catch(() => setListe([]));
  useEffect(() => { charger(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!liste?.length) return null;
  return (
    <section className="space-y-3" data-testid="carnet-maitres-ouvrage">
      <div>
        <h3 className="text-base font-bold text-[#0a3d62]">Mes maîtres d'ouvrage</h3>
        <p className="text-xs text-slate-500">Vos clients et les chantiers assurés en RCDS BATICO nominative</p>
      </div>
      <div className="space-y-2">{liste.map((mo, i) => <MaitreOuvrage key={mo.cle || i} mo={mo} onVoir={setDoc} onMaj={charger} />)}</div>
      {doc && (
        <VisionneuseProtegee url={`${API}/souscriptions/${doc.souscription_id}/download-document`}
          titre={`Attestation — ${doc.numero_police}`} onClose={() => setDoc(null)} />
      )}
    </section>
  );
}
